import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import type { Group } from 'three';

type OrbiterConfig = {
  radius: number;
  speed: number;
  offset: number;
  height: number;
  size: number;
  hue: number;
};

const createOrbiters = () => {
  const count = 14;
  const list: OrbiterConfig[] = [];
  for (let i = 0; i < count; i += 1) {
    const radius = 2 + (i % 4) * 0.45;
    const speed = 0.18 + i * 0.02;
    const offset = (Math.PI * 2 * i) / count;
    const height = -0.6 + (i % 3) * 0.6;
    const size = 0.08 + (i % 4) * 0.025;
    // Use orange color range (15-45) for bubbles
    const hue = 15 + (i * 2.5) % 30;
    list.push({ radius, speed, offset, height, size, hue });
  }
  return list;
};

const orbiters = createOrbiters();

export const OrbitalGallery: React.FC = () => {
  const groupRef = useRef<Group | null>(null);

  useFrame((state) => {
    if (!groupRef.current) return;
    const t = state.clock.getElapsedTime();
    groupRef.current.children.forEach((child, index) => {
      const config = orbiters[index];
      if (!config) return;
      const angle = t * config.speed + config.offset;
      const x = Math.cos(angle) * config.radius;
      const z = Math.sin(angle) * config.radius;
      const y = config.height + Math.sin(t * 0.7 + index) * 0.18;
      child.position.set(x, y, z);
    });
  });

  return (
    <group ref={groupRef}>
      <mesh position={[0, 0, 0]}>
        <sphereGeometry args={[0.9, 40, 40]} />
        <meshStandardMaterial
          color="#FF6B35"
          emissive="#FF4500"
          emissiveIntensity={1.2}
          roughness={0.2}
          metalness={0.7}
        />
      </mesh>
      {orbiters.map((config, index) => {
        const color = `hsl(${config.hue}, 95%, 62%)`;
        const emissive = `hsl(${config.hue}, 95%, 55%)`;
        return (
          <mesh key={index}>
            <sphereGeometry args={[config.size, 24, 24]} />
            <meshStandardMaterial
              color={color}
              emissive={emissive}
              emissiveIntensity={1}
              roughness={0.25}
              metalness={0.65}
            />
          </mesh>
        );
      })}
      <mesh position={[0, -1.2, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[1.4, 3.6, 64]} />
        <meshStandardMaterial
          color="#FF6B35"
          emissive="#FF4500"
          emissiveIntensity={0.4}
          transparent
          opacity={0.9}
        />
      </mesh>
    </group>
  );
};

